import * as React from 'react';
import { Component } from 'react';
import './Sidebar.scss';
import { AppUser } from 'Src/views/App/App';
import {
  isUserAdminSU,
  isUserCoach,
  isUserManager,
  isUserParent,
  isUserTeacher,
  isUserStudent
} from '../../helpers/user/user';
import { SidebarItem } from './SidebarItem';
import { SidebarItems } from './SidebarItems';
import {
  getSidebarItemsAdmin,
  getSidebarItemsAdminSU,
  getSidebarItemsCoach,
  getSidebarItemsParent,
  getSidebarItemsTeacher,
  getSidebarItemsStudent,
  getSidebarItemsManager
} from '../../consts/sidebar';
import { SCHOOL_SUBSCRIPTION_TYPE } from '../../types/school';
import { BurgerButton } from 'Src/components/BurgerButton/BurgerButton';
import { WINDOW_BREAKPOINT_FOR_SIDEBAR } from 'Src/consts/responsive';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ROLE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/user';
import { Role } from 'Src/models/roles';
import { isModuleActive, isModuleSubscriptionActive } from 'Src/helpers/modules/moduleHelpers';
import { getStudentCurrentTeams } from 'Src/helpers/service/student/teams';
import { getChildrenCurrentTeams } from 'Src/helpers/service/parent/teams';

interface Props extends RouteComponentProps {
  user: AppUser;
  needUpdate: boolean;
  unsetNeedUpdate: () => void;
  onSidebarCloseClick: () => void;
  windowWidth: number;
  availableRoles: Role[];
  countBadges: {
    requests: string;
    bookings: string;
    inboxMessages: string;
    invites: string;
    tournamentInvites: string;
    waitingList: string;
    upcomingTournaments: string;
    availabilityReports: string;
    absenceRequests: string;
    tournamentConfirmationRequests: string;
  };
}

export interface SidebarItemExpandable {
  title: string;
  items: SimpleSidebarItem[];
}

export interface SimpleSidebarItem {
  to: {
    pathname: string;
    search?: string;
  };
  itemTitle: string;
  visibleToSchoolSubscriptions: SCHOOL_SUBSCRIPTION_TYPE[];
  notificationCount?: string;
  isNew?: boolean;
  activePathPrefixes?: string[];
  icon?: 'home' | 'calendar' | 'children' | 'awards' | 'consent' | 'clubs' | 'availability' | 'absence' | 'teams';
  alwaysVisible?: boolean;
}

const SidebarComponent = (props: Props) => {
  const { user, countBadges, onSidebarCloseClick, windowWidth, location, availableRoles } = props;

  const isAbsenceRequestsEnabled = (availableRoles || []).some(
    role =>
      role.name === 'PARENT' &&
      (role.permissions || []).some(
        permission =>
          typeof (permission as any).studentId !== 'undefined' && !!(permission.school as any)?.isAbsenceRequestEnabled
      )
  );
  const isParentAwardsEnabled = (availableRoles || []).some(
    role =>
      role.name === 'PARENT' &&
      (role.permissions || []).some(permission => {
        if (typeof (permission as any).studentId === 'undefined') return false;
        const subscriptions = (permission.school as any)?.moduleSubscriptions || [];
        const awardsSubscription = subscriptions.find(subscription => subscription.module === 'AWARDS');
        return isModuleSubscriptionActive(awardsSubscription);
      })
  );
  const isStudentAwardsEnabled = isModuleActive(user, 'AWARDS');
  const [hasCurrentTeams, setHasCurrentTeams] = React.useState(false);
  const [hasChildrenCurrentTeams, setHasChildrenCurrentTeams] = React.useState(false);
  React.useEffect(() => {
    if (!isUserStudent(user)) return;
    let mounted = true;
    getStudentCurrentTeams(user).then(data => mounted && setHasCurrentTeams(data.total > 0)).catch(() => mounted && setHasCurrentTeams(false));
    return () => { mounted = false; };
  }, [user.roleSessionKey, user.activeSchoolId, user.activeRole]);
  React.useEffect(() => {
    if (!isUserParent(user)) return;
    let mounted = true;
    getChildrenCurrentTeams(user).then(data => mounted && setHasChildrenCurrentTeams(data.total > 0)).catch(() => mounted && setHasChildrenCurrentTeams(false));
    return () => { mounted = false; };
  }, [user.roleSessionKey, user.activeSchoolId, user.activeRole]);

  const getSidebarItems = (): (SidebarItemExpandable | SimpleSidebarItem)[] => {
    switch (true) {
      case isUserParent(user):
        return getSidebarItemsParent(countBadges).filter(
          item =>
            ((item as SimpleSidebarItem).to?.pathname !== '/absenceRequests/outbox' || isAbsenceRequestsEnabled) &&
            ((item as SimpleSidebarItem).to?.pathname !== '/awards/current' || isParentAwardsEnabled) &&
            ((item as SimpleSidebarItem).to?.pathname !== '/childrenTeams' || hasChildrenCurrentTeams)
        );
      case isUserCoach(user):
        return getSidebarItemsCoach(countBadges);
      case isUserTeacher(user):
        return getSidebarItemsTeacher(countBadges);
      case isUserAdminSU(user):
        return getSidebarItemsAdminSU(countBadges);
      case isUserStudent(user):
        return getSidebarItemsStudent(countBadges).filter(
          item => ((item as SimpleSidebarItem).to?.pathname !== '/awards/current' || isStudentAwardsEnabled) &&
            ((item as SimpleSidebarItem).to?.pathname !== '/myTeams' || hasCurrentTeams)
        );
      case isUserManager(user):
        return getSidebarItemsManager(countBadges);
      default:
        return getSidebarItemsAdmin(countBadges);
    }
  };

  const isSidebarItemExpandable = (arg: any): arg is SidebarItemExpandable => {
    return Array.isArray(arg.items);
  };

  const rawSidebarItems: (SidebarItemExpandable | SimpleSidebarItem)[] = getSidebarItems();
  const normalizedSidebarItems: (SidebarItemExpandable | SimpleSidebarItem)[] = rawSidebarItems.map(item => {
    if (!isSidebarItemExpandable(item)) return item;
    const tabbedRoot =
      item.title === 'Tournaments'
        ? '/tournaments'
        : item.title === 'High-level tournaments'
        ? '/highLevelTournaments'
        : item.title === 'Messages'
        ? '/messages'
        : undefined;
    if (tabbedRoot) {
      return {
        itemTitle: item.title,
        to: item.items[0].to,
        visibleToSchoolSubscriptions: item.items[0].visibleToSchoolSubscriptions,
        notificationCount: item.items.reduce((sum, child) => sum + Number(child.notificationCount || 0), 0).toString(),
        activePathPrefixes: tabbedRoot === '/tournaments' ? [tabbedRoot, '/tournamentAvailability'] : [tabbedRoot]
      };
    }
    if (item.items.length === 1) {
      return { ...item.items[0], itemTitle: item.title };
    }
    return item;
  });
  const sidebarItems = normalizedSidebarItems;
  const isPathInGroup = (item: SidebarItemExpandable): boolean =>
    item.items.some(child => location.pathname.indexOf(child.to.pathname) === 0);
  const currentGroup = sidebarItems.find(item => isSidebarItemExpandable(item) && isPathInGroup(item));
  const [openGroup, setOpenGroup] = React.useState<string | null>(
    currentGroup && isSidebarItemExpandable(currentGroup) ? currentGroup.title : null
  );

  React.useEffect(() => {
    const next = sidebarItems.find(item => isSidebarItemExpandable(item) && isPathInGroup(item));
    setOpenGroup(next && isSidebarItemExpandable(next) ? next.title : null);
  }, [location.pathname]);

  const roleName = ROLE_SERVER_TO_CLIENT_MAPPING[user.activeRole] || user.activeRole || 'Role';

  return (
    <>
      <div className="bSidebar">
        {windowWidth < WINDOW_BREAKPOINT_FOR_SIDEBAR && (
          <div className="d-flex justify-content-end mb-2">
            <BurgerButton onClick={onSidebarCloseClick} />
          </div>
        )}
        {sidebarItems.map((sidebarItem, index) => (
          <React.Fragment key={`sidebar_entry_${index}`}>
            {isSidebarItemExpandable(sidebarItem) ? (
              <SidebarItems
                item={sidebarItem}
                user={user}
                isOpen={openGroup === sidebarItem.title}
                isCurrentGroup={isPathInGroup(sidebarItem)}
                onToggle={() => setOpenGroup(openGroup === sidebarItem.title ? null : sidebarItem.title)}
              />
            ) : (
              <SidebarItem item={sidebarItem} user={user} />
            )}
          </React.Fragment>
        ))}
        {
          <div className="eSidebarAccount">
            <div className="eSidebarAccount__avatar">{(user.activeSchoolName || 'U').charAt(0).toUpperCase()}</div>
            <div className="eSidebarAccount__details">
              <strong>{user.activeSchoolName || 'Union'}</strong>
              <span>{roleName}</span>
            </div>
            <button
              type="button"
              onClick={onSidebarCloseClick}
              title="Collapse navigation"
              aria-label="Collapse navigation"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 6l-6 6 6 6" />
              </svg>
            </button>
          </div>
        }
      </div>
      <div className="bSidebarOverlay" onClick={onSidebarCloseClick}></div>
    </>
  );
};

export const Sidebar = withRouter(SidebarComponent);
Sidebar.displayName = 'Sidebar';
